import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";

import PCContainer from "../../components/atoms/layout/pcContainer";

import LSBdDisplay02 from "../../components/molecules/text/linSeedKr/Bd/lsBdDisplay02";
import LSBdH2HeadLine from "../../components/molecules/text/linSeedKr/Bd/lsBdH2HeadLine";
import LSRgH1HeadLine from "../../components/molecules/text/linSeedKr/Rg/lsRgH1HeadLine";
import LSBdH1HeadLine from "../../components/molecules/text/linSeedKr/Bd/lsBdH1HeadLine";
import PaymentInfo from "../../components/templates/lecture/paymentInfo";

import useDisplay from "../../hooks/useDisplay";
import { fileUrl } from "../../api/api";
import { formatNumberWithCommas } from "../../util/formatNumber";
import { errorHandler } from "../../util/errorHandler";
import LectureApi from "../../api/lecture.api";
import TermApi from "../../api/term.api";
import LSRgBody02 from "../../components/molecules/text/linSeedKr/Rg/lsRgBody02";
import CouponApi from "../../api/coupon.api";
import SelecteBoxValue from "../../components/organisms/selectbox/selecteBoxValue";
import { route } from "../../router/route";
import { toast } from "react-toastify";
import Loading from "../../components/templates/loading";

const LecturePayment = () => {
  const { id } = useParams();
  const { isMobile, isTablet, isDesktop } = useDisplay();
  const isSmall = isMobile || isTablet;

  const navigate = useNavigate();

  // ---------- 리뷰 점수 관련 ---------- //
  const [couponBoxOpen, setCouponBoxOpen] = useState(false);
  const [couponValue, setCouponValue] = useState();
  const [couponList, setCouponList] = useState([]);
  const [couponSalePrice, setCouponSalePrice] = useState(0);

  const [lectureInfo, setLectureInfo] = useState();
  const [learningTermInfo, setLearningTermInfo] = useState();
  const [refundTermInfo, setRefundTermInfo] = useState();

  const [paymentMethodValue, setPaymentMethodValue] = useState("CARD");
  const [realPay, setRealPay] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    if (sessionStorage.getItem("isLogin") !== "true") {
      toast("강의를 구매하려면 로그인을 해주세요.");
      navigate(route.login, {
        replace: true,
        state: { path: window.location.href },
      });
      return;
    }

    if (!/^\d+$/.test(id) || Number(id) < 1) {
      toast("올바르지 않은 강의입니다.");
      navigate(route.lecture_list, { replace: true });
      return;
    }

    const loadPaymentPage = async () => {
      try {
        await Promise.all([
          getLectureInfo(),
          getLearningTermInfo(),
          getRefundTermInfo(),
          getCouponList(),
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    loadPaymentPage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // totalAmount: 총 금액, selectCouponAllValue.coupon.amount: 할인율 (%)
  function calculateDiscountedTotal(totalAmount, discountPercentage) {
    // 할인된 금액을 계산하고 소수점은 올림 처리
    const discountAmount = Math.ceil(totalAmount * (discountPercentage / 100));

    return discountAmount;
  }

  useEffect(() => {
    if (lectureInfo) {
      if (Number(couponValue) > 0) {
        let coupon = couponList.find((item) => item.id === couponValue);
        if (!coupon) return;
        let discountAmount;
        if (coupon.coupon.couponType === "RATIO") {
          discountAmount = calculateDiscountedTotal(
            lectureInfo.price,
            Number(coupon.coupon.amount),
          );
        } else {
          discountAmount = Number(coupon.coupon.amount);
        }

        discountAmount = Math.min(
          lectureInfo.price,
          Math.max(0, discountAmount),
        );
        setCouponSalePrice(discountAmount);
        setRealPay(Math.max(0, lectureInfo.price - discountAmount));
      } else {
        setCouponSalePrice(0);
        setRealPay(lectureInfo.price);
      }
    }
  }, [couponList, couponValue, lectureInfo]);

  const getLectureInfo = async () => {
    try {
      const response = (await LectureApi.getDetail(id)).data.data.content;

      if (
        response?.lectureCategory?.lectureCategoryType !== "PAID_LECTURE" ||
        Number(response?.price) <= 0
      ) {
        toast("결제가 필요하지 않은 강의입니다.");
        navigate(`${route.lecture_detail}/${id}`, { replace: true });
        return;
      }

      const checkResponse = (
        await LectureApi.lectureCheck({ lectureVideoId: Number(id) })
      ).data.data;
      if (checkResponse.existFlag) {
        toast("이미 수강 중인 강의입니다.");
        navigate(`${route.my_page_classroom}?category=PAID_LECTURE`, {
          replace: true,
        });
        return;
      }

      setLectureInfo(response);
    } catch (error) {
      errorHandler(error);
      navigate(route.lecture_list, { replace: true });
    }
  };

  const getLearningTermInfo = async () => {
    try {
      const response = (await TermApi.getDetail(6)).data.data.content;
      setLearningTermInfo(response);
    } catch (error) {
      errorHandler(error);
    }
  };

  const getRefundTermInfo = async () => {
    try {
      const response = (await TermApi.getDetail(5)).data.data.content;
      setRefundTermInfo(response);
    } catch (error) {
      errorHandler(error);
    }
  };

  const getCouponList = async () => {
    try {
      const data = {
        page: 1,
        size: 0,
        couponStatus: "POSSIBLE",
      };
      const response = (await CouponApi.getList(data)).data.data.content;

      const temp = (response ?? [])
        .filter((item) => item.coupon)
        .map((item) => {
          return {
            ...item,
            title: item.coupon.title,
            value: item.id,
          };
        });
      setCouponList([{ id: -1, value: -1, title: "쿠폰 사용 안 함" }, ...temp]);
    } catch (error) {
      errorHandler(error);
    }
  };

  if (isLoading || !lectureInfo) return <Loading />;

  return (
    <>
      <LecturePaymentSection>
        <PCContainer
          style={{
            flexDirection: "column",
            padding: isMobile
              ? "40px 20px 80px 20px"
              : isTablet
                ? "40px 60px 80px 60px"
                : "80px 0px 150px 0px",
            width: "100%",
          }}
        >
          <LSBdDisplay02
            style={{
              color: "#000000",
              marginBottom: isSmall ? 10 : 32,
            }}
          >
            강의 구매
          </LSBdDisplay02>

          <div
            style={{
              display: "flex",
              flexDirection: isSmall ? "column" : "row",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div
              style={{
                width: isSmall ? "100%" : 668,
              }}
            >
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <img
                    src={
                      lectureInfo?.thumbnailServerFileName
                        ? `${fileUrl}/${lectureInfo?.thumbnailServerFileName}`
                        : ""
                    }
                    style={{
                      width: isMobile ? 158 : 278,
                      height: isMobile ? 101 : 177,
                      marginRight: isSmall ? 20 : 30,
                      borderRadius: 15,
                    }}
                    alt="video"
                  />
                  <div>
                    <LSBdDisplay02
                      style={{
                        color: "#000000",
                        marginBottom: isMobile ? 10 : 30,
                        marginTop: 4,
                        fontSize: isMobile ? 16 : 24,
                        lineHeight: isMobile ? "22.4px" : "33.6px",
                      }}
                    >
                      {lectureInfo?.title}
                    </LSBdDisplay02>
                    <TopTextContain>
                      <TopRightText
                        style={{
                          fontSize: isMobile ? 12 : 16,
                          lineHeight: isMobile ? "14.4px" : "22.4px",
                        }}
                      >
                        수강기간
                      </TopRightText>
                      <TopLeftText
                        style={{
                          fontSize: isMobile ? 12 : 16,
                          lineHeight: isMobile ? "14.4px" : "22.4px",
                        }}
                      >
                        {lectureInfo?.limitFlag
                          ? "무제한"
                          : `${formatNumberWithCommas(lectureInfo?.period)}일`}
                      </TopLeftText>
                    </TopTextContain>
                    <TopTextContain>
                      <TopRightText
                        style={{
                          fontSize: isMobile ? 12 : 16,
                          lineHeight: isMobile ? "14.4px" : "22.4px",
                        }}
                      >
                        커리큘럼
                      </TopRightText>
                      <TopLeftText
                        style={{
                          fontSize: isMobile ? 12 : 16,
                          lineHeight: isMobile ? "14.4px" : "22.4px",
                        }}
                      >
                        총{" "}
                        {formatNumberWithCommas(lectureInfo?.curriculumCount)}강
                      </TopLeftText>
                    </TopTextContain>
                    <TopTextContain
                      style={{
                        marginBottom: 0,
                      }}
                    >
                      <TopRightText
                        style={{
                          fontSize: isMobile ? 12 : 16,
                          lineHeight: isMobile ? "14.4px" : "22.4px",
                        }}
                      >
                        강사명
                      </TopRightText>
                      <TopLeftText
                        style={{
                          fontSize: isMobile ? 12 : 16,
                          lineHeight: isMobile ? "14.4px" : "22.4px",
                        }}
                      >
                        {lectureInfo?.instructorName}
                      </TopLeftText>
                    </TopTextContain>
                  </div>
                </div>

                <WhiteBoxContain
                  $isMobile={isMobile}
                  style={{
                    marginTop: isMobile ? 20 : 40,
                  }}
                >
                  <LSBdH2HeadLine
                    style={{
                      color: "#5050FF",
                      paddingBottom: 16,
                      fontSize: isMobile ? 15 : 18,
                      lineHeight: isMobile ? "21px" : "25.2px",
                      borderBottom: isMobile ? "1px solid #DBDDE0" : "none",
                      marginBottom: isMobile ? 10 : 0,
                    }}
                  >
                    쿠폰 사용
                  </LSBdH2HeadLine>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <LSRgH1HeadLine
                      style={{
                        color: "#000000",
                        fontSize: isMobile ? 12 : 16,
                        lineHeight: isMobile ? "14.4px" : "22.4px",
                      }}
                    >
                      보유 쿠폰
                    </LSRgH1HeadLine>

                    <LSBdH1HeadLine
                      style={{
                        color: "#000000",
                        fontSize: isMobile ? 12 : 16,
                        lineHeight: isMobile ? "14.4px" : "22.4px",
                      }}
                    >
                      총 {Math.max(0, couponList.length - 1)}개
                    </LSBdH1HeadLine>
                  </div>

                  <SelecteBoxValue
                    disabled={
                      sessionStorage.getItem("membership") === "true"
                        ? true
                        : false
                    }
                    style={{
                      marginTop: 16,
                    }}
                    selectedValue={couponValue}
                    isOpen={couponBoxOpen}
                    wrapperHandler={() => {
                      setCouponBoxOpen(!couponBoxOpen);
                    }}
                    itemList={couponList}
                    selectedValueSetterFunc={setCouponValue}
                    placeholder={"쿠폰을 선택해주세요."}
                  />
                </WhiteBoxContain>

                <WhiteBoxContain
                  $isMobile={isMobile}
                  style={{
                    marginTop: isMobile ? 20 : 40,
                  }}
                >
                  <LSBdH2HeadLine
                    style={{
                      color: "#5050FF",
                      paddingBottom: 16,
                      fontSize: isMobile ? 15 : 18,
                      lineHeight: isMobile ? "21px" : "25.2px",
                      borderBottom: isMobile ? "1px solid #DBDDE0" : "none",
                      marginBottom: isMobile ? 10 : 0,
                    }}
                  >
                    결제 수단
                  </LSBdH2HeadLine>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: isSmall ? "10px 11px" : "20px 14px",
                    }}
                  >
                    <div
                      onClick={() => {
                        setPaymentMethodValue("CARD");
                      }}
                      style={{
                        borderRadius: 8,
                        width: isSmall ? "46.8%" : 192,
                        height: 45,
                        border:
                          paymentMethodValue === "CARD"
                            ? "1px solid #5050FF"
                            : "1px solid #DBDDE0",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                    >
                      <LSRgBody02
                        style={{
                          color:
                            paymentMethodValue === "CARD"
                              ? "#5050FF"
                              : "#404249",
                        }}
                      >
                        신용・체크 카드
                      </LSRgBody02>
                    </div>
                    {/* <img
                      onClick={() => {
                        setPaymentMethodValue("KAKAOPAY");
                      }}
                      src={
                        isMobile
                          ? "/assets/images/kakaopay_m.png"
                          : "/assets/images/kakaopay.png"
                      }
                      style={{
                        width: isSmall ? "46.8%" : 192,
                        height: 45,
                        cursor: "pointer",
                        border:
                          paymentMethodValue === "KAKAOPAY"
                            ? "1px solid #5050FF"
                            : "none",
                        borderRadius: 8,
                      }}
                      alt="kakaopay"
                    />
                    <img
                      onClick={() => {
                        setPaymentMethodValue("TOSSPAY");
                      }}
                      src={
                        isMobile
                          ? "/assets/images/tosspay_m.png"
                          : "/assets/images/tosspay.png"
                      }
                      style={{
                        width: isSmall ? "46.8%" : 192,
                        height: 45,
                        cursor: "pointer",
                        border:
                          paymentMethodValue === "TOSSPAY"
                            ? "1px solid #5050FF"
                            : "none",
                        borderRadius: 8,
                      }}
                      alt="tosspay"
                    />
                    <img
                      onClick={() => {
                        setPaymentMethodValue("NAVERPAY");
                      }}
                      src={
                        isMobile
                          ? "/assets/images/naverpay_m.png"
                          : "/assets/images/naverpay.png"
                      }
                      style={{
                        width: isSmall ? "46.8%" : 192,
                        height: 45,
                        cursor: "pointer",
                        border:
                          paymentMethodValue === "NAVERPAY"
                            ? "1px solid #5050FF"
                            : "none",
                        borderRadius: 8,
                      }}
                      alt="naverpay"
                    /> */}
                  </div>
                </WhiteBoxContain>

                {isSmall ? (
                  <PaymentInfo
                    lectureInfo={lectureInfo}
                    paymentMethodValue={paymentMethodValue}
                    realPay={realPay}
                    couponSalePrice={couponSalePrice}
                    couponValue={couponValue}
                    isConsulting={false}
                  />
                ) : null}

                <WhiteBoxContain
                  $isMobile={isMobile}
                  style={{
                    marginTop: isMobile ? 20 : 30,
                  }}
                >
                  <LSBdH2HeadLine
                    style={{
                      color: "#5050FF",
                      paddingBottom: 16,
                      fontSize: isMobile ? 15 : 18,
                      lineHeight: isMobile ? "21px" : "25.2px",
                      borderBottom: isMobile ? "1px solid #DBDDE0" : "none",
                      marginBottom: isMobile ? 10 : 0,
                    }}
                  >
                    학습규정
                  </LSBdH2HeadLine>

                  <LSRgH1HeadLine
                    className="ql-editor"
                    style={{
                      color: "#000000",
                      fontSize: isMobile ? 12 : 16,
                      lineHeight: isMobile ? "14.4px" : "22.4px",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: learningTermInfo?.content,
                    }}
                  />
                </WhiteBoxContain>

                <WhiteBoxContain
                  $isMobile={isMobile}
                  style={{
                    marginTop: isMobile ? 20 : 30,
                  }}
                >
                  <LSBdH2HeadLine
                    style={{
                      color: "#5050FF",
                      paddingBottom: 16,
                      fontSize: isMobile ? 15 : 18,
                      lineHeight: isMobile ? "21px" : "25.2px",
                      borderBottom: isMobile ? "1px solid #DBDDE0" : "none",
                      marginBottom: isMobile ? 10 : 0,
                    }}
                  >
                    환불규정
                  </LSBdH2HeadLine>

                  <LSRgH1HeadLine
                    className="ql-editor"
                    style={{
                      color: "#000000",
                      fontSize: isMobile ? 12 : 16,
                      lineHeight: isMobile ? "14.4px" : "22.4px",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: refundTermInfo?.content,
                    }}
                  />
                </WhiteBoxContain>
              </div>
            </div>

            {isSmall ? null : (
              <PaymentInfo
                lectureInfo={lectureInfo}
                paymentMethodValue={paymentMethodValue}
                realPay={realPay}
                couponSalePrice={couponSalePrice}
                couponValue={couponValue}
                isConsulting={false}
              />
            )}
          </div>
        </PCContainer>
      </LecturePaymentSection>
    </>
  );
};

export default LecturePayment;

const LecturePaymentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  background: #ededff80;
`;

const WhiteBoxContain = styled.div`
  background: rgb(255, 255, 255);
  box-shadow: rgba(0, 0, 0, 0.1) 0px 0px 6px 0px;
  padding: ${(props) => (props.$isMobile ? "20px" : "40px 30px")};
  width: 100%;
  border-radius: ${(props) => (props.$isMobile ? "10px" : "15px")};
`;

const TopTextContain = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
`;

const TopRightText = styled(LSBdH1HeadLine)`
  width: 55px;
  margin-right: 18px;
  color: #898e99;
`;

const TopLeftText = styled(LSRgH1HeadLine)`
  color: #000000;
`;
